import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { Controller, ControllerRenderProps } from 'react-hook-form'

import { Cross1Icon } from '@radix-ui/react-icons'

import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'
import type { TFileField } from './file-field'
import type { TStreamingFileField } from './streaming-file-field'

type TFileRowField = TFileField<any> | TStreamingFileField<any>

type TFileInputProps = {
    field: ControllerRenderProps<any, string>
    rowField: TFileRowField
    required: boolean
    disabled: boolean
}

/**
 * The form value is the picked `File`; before anything is picked it holds whatever `data` had
 * (the name of the stored file), which is only displayed.
 */
export const FileInput = ({
    field: { value, onChange, ref, disabled: _disabled, ...field },
    rowField,
    required,
    disabled,
}: TFileInputProps) => {
    const t = useTranslations('form.file-field')
    const inputRef = useRef<HTMLInputElement>(null)

    // a form reset only changes the value; the DOM input would keep the old selection
    useEffect(() => {
        if (!(value instanceof File) && inputRef.current)
            inputRef.current.value = ''
    }, [value])

    const fileName = value instanceof File ? value.name : typeof value === 'string' ? value : undefined

    const change = (file: File | undefined) => {
        onChange(file)
        rowField.onChange?.(file)
    }

    return (
        <>
            <label>
                <input
                    id={field.name}
                    {...field}
                    {...rowField.props}
                    ref={element => {
                        inputRef.current = element
                        ref(element)
                    }}
                    type="file"
                    required={required}
                    disabled={disabled}
                    onChange={event => change(event.target.files?.[0] ?? undefined)}
                />

                <span>{t('select-file')}</span>
            </label>
            {fileName && (
                <>
                    <span>{fileName}</span>
                    {!disabled && <Cross1Icon onClick={() => change(undefined)} />}
                </>
            )}
        </>
    )
}

type TFileFieldRowProps = {
    rowField: TFileRowField
    fieldName: string
    labelWidth?: string
}

/** Shared row of the `file` and `streaming-file` fields. */
export const FileFieldRow = ({ rowField, fieldName, labelWidth }: TFileFieldRowProps) => {
    const { disabled, required } = useFieldState(rowField)

    return (
        <FieldRow fieldName={fieldName} label={rowField.label} required={required} labelWidth={labelWidth}>
            <div className="form-file-input">
                <Controller
                    name={fieldName}
                    rules={getFieldRules(rowField, required)}
                    render={({ field }) => (
                        <FileInput field={field} rowField={rowField} required={required} disabled={disabled} />
                    )}
                />
            </div>
        </FieldRow>
    )
}
